import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile, rendered explicitly.
 *
 * Explicit rather than the automatic `cf-turnstile` class scanner: the scanner
 * walks the DOM when the script loads and wires up whatever it finds, which
 * fights React over ownership of the node. Under StrictMode's double-mount, or
 * any re-render that replaces the container, that leaves an orphaned widget and
 * a second one drawn beneath it. Rendering by hand means this component owns
 * the widget's whole life and can remove it.
 *
 * WHAT THE CALLER HAS TO KNOW
 *
 * A token is single-use and expires after a few minutes. So a failed submit
 * must reset the widget before the visitor tries again, or the second attempt
 * sends a token Cloudflare has already retired and fails identically - which
 * reads as "the site is broken" rather than "try again". Call the function
 * handed to `onReady` to do that.
 *
 * The demo page sets its colours explicitly and is always light - see the
 * signed-out pages note in styles.css - so the widget is pinned to light
 * rather than following a theme this page does not have.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/* One load for the document, shared by every mount. Kept outside the component
   so a remount does not append a second <script>. */
let scriptPromise = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve(window.turnstile);
    el.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error('Could not load the verification widget'));
    };
    document.head.appendChild(el);
  });

  return scriptPromise;
}

/**
 * @param {string}   siteKey    from GET /auth/demo/config
 * @param {Function} onToken    called with the solved token, or null when it
 *                              expires or errors — so the caller can disable
 *                              submit again rather than sending a dead token
 * @param {Function} [onReady]  receives a reset function
 * @param {Function} [onError]  called when the widget cannot load at all
 */
export default function Turnstile({ siteKey, onToken, onReady, onError }) {
  const holder = useRef(null);

  /* Callbacks in refs, so changing them does not tear down and re-render the
     widget. A parent that passes an inline arrow would otherwise reset the
     challenge on every keystroke in the email field. */
  const cbs = useRef({ onToken, onReady, onError });
  cbs.current = { onToken, onReady, onError };

  useEffect(() => {
    if (!siteKey) return;

    let widgetId = null;
    let cancelled = false;

    loadTurnstile()
      .then(turnstile => {
        if (cancelled || !holder.current) return;

        widgetId = turnstile.render(holder.current, {
          sitekey: siteKey,
          theme: 'light',
          action: 'demo-request',
          callback: token => cbs.current.onToken?.(token),
          // Both mean "the token you have is no longer good". Clearing it is
          // what re-disables the submit button.
          'expired-callback': () => cbs.current.onToken?.(null),
          'error-callback': () => cbs.current.onToken?.(null)
        });

        cbs.current.onReady?.(() => {
          try { turnstile.reset(widgetId); } catch { /* already gone */ }
          cbs.current.onToken?.(null);
        });
      })
      .catch(err => { if (!cancelled) cbs.current.onError?.(err); });

    return () => {
      cancelled = true;
      if (widgetId !== null) {
        try { window.turnstile?.remove(widgetId); } catch { /* already gone */ }
      }
    };
  }, [siteKey]);

  return <div ref={holder} className="public-turnstile" />;
}
